import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { Button, Modal } from '@/shared/ui';
import { tendersApi } from '@/shared/api/endpoints/tenders';
import { STARTER_WELCOME_DISMISS_KEY } from '@/widgets/starter-welcome-modal/StarterWelcomeModal';

const DISMISS_KEY = 'tenderts.matchingSetup.dismissed';
const SETUP_PATH = '/app/tenders/setup';
const SKIP_PREFIXES = [
  SETUP_PATH,
  '/app/tenders/onboarding',
  '/app/tenders/schedules',
  '/app/tenders/tariff',
  '/app/tenders/orders',
  '/app/tenders/billing',
];

function wasDismissed(): boolean {
  try {
    return sessionStorage.getItem(DISMISS_KEY) === '1';
  } catch {
    return false;
  }
}

function starterWelcomePending(): boolean {
  try {
    return localStorage.getItem(STARTER_WELCOME_DISMISS_KEY) !== '1';
  } catch {
    return false;
  }
}

function markDismissed() {
  try {
    sessionStorage.setItem(DISMISS_KEY, '1');
  } catch {
    /* ignore */
  }
}

function shouldSkipPath(pathname: string): boolean {
  return SKIP_PREFIXES.some((p) => pathname === p || pathname.startsWith(`${p}/`));
}

function inPromptArea(pathname: string): boolean {
  if (pathname === '/app' || pathname === '/app/dashboard') return true;
  return pathname === '/app/tenders' || pathname.startsWith('/app/tenders/');
}

/**
 * Prompt users with zero schedules to configure automatic matching via the setup wizard.
 */
export function NoMatchingSetupModal() {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const skip = shouldSkipPath(location.pathname) || !inPromptArea(location.pathname);
  const tariff = useQuery({
    queryKey: ['tender-tariff', 'starter-welcome'],
    queryFn: () => tendersApi.tariff(),
    enabled: !skip && starterWelcomePending(),
    staleTime: 60_000,
    retry: false,
  });
  const waitForStarter =
    starterWelcomePending() &&
    (tariff.isLoading ||
      (tariff.isSuccess &&
        (tariff.data?.subscription?.plan_code ?? '').trim().toLowerCase() === 'starter'));

  const { data, isSuccess, isError } = useQuery({
    queryKey: ['tender-schedules'],
    queryFn: () => tendersApi.cabinetGet<{ items?: unknown[] }>('api/schedules'),
    enabled: !skip && !waitForStarter,
    staleTime: 30_000,
    retry: false,
  });

  const empty = isSuccess && (data?.items?.length ?? 0) === 0;

  useEffect(() => {
    if (skip || waitForStarter || isError || !empty || wasDismissed()) {
      setOpen(false);
      return;
    }
    setOpen(true);
  }, [skip, waitForStarter, isError, empty, location.pathname]);

  const onClose = () => {
    markDismissed();
    setOpen(false);
  };

  const onSetup = () => {
    markDismissed();
    setOpen(false);
    navigate(SETUP_PATH);
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t('tenders.matchingSetupNeededTitle')}
      footer={
        <>
          <Button type="button" variant="secondary" onClick={onClose}>
            {t('common.later')}
          </Button>
          <Button type="button" onClick={onSetup}>
            {t('tenders.matchingSetupNeededCta')}
          </Button>
        </>
      }
    >
      <p className="text-sm text-text-secondary">{t('tenders.matchingSetupNeededBody')}</p>
    </Modal>
  );
}
